import { useMemo, useState, type ReactNode } from 'react';
import { Check, CheckCheck, ExternalLink, Loader2, RotateCcw, X } from 'lucide-react';
import type { OptimizationReport, OptimizationSuggestionState } from '@monitoring-panel/types';
import { ReportSection } from '@/components/report/ReportSection';
import type { SuggestionDecision } from '@/services/api';
import { useOptimizationQueue } from '@/hooks/useOptimizationQueue';
import type { QueueJob } from '@/store/optimizationQueue';
import { TableFrame, Th, Td } from './TableFrame';
import { dateTime, formatSuggestionValue, fullDate } from './format';
import { adviaOsRecordUrl, dv360Context, dv360Url, type Dv360Context } from './links';
import { LEVEL_LABELS, leverLabel, priorityTag, STATUS_TAGS } from './labels';
import { asDeliveryReport } from './types';

interface SuggestionsTabProps {
  executionId: string;
  report: OptimizationReport;
}

/** Checkbox · Cambio · Entidad · Antes → después · Motivo (the rest) · Estado y acciones. */
const COLUMN_WIDTHS = ['40px', '15%', '19%', '15%', undefined, '190px'];

/** A suggestion a trader can still act on: pending, not informative, nothing queued for it. */
export function isSelectable(suggestion: OptimizationSuggestionState, queued: boolean): boolean {
  return suggestion.status === 'pending' && suggestion.lever !== 'kpi' && !queued;
}

/** Applied (in DV360 or by hand) but the Advia OS record failed. */
function needsRecordRetry(suggestion: OptimizationSuggestionState): boolean {
  return suggestion.status === 'applied' && suggestion.error !== null && suggestion.adviaOsRecordId === null;
}

export function SuggestionsTab({ executionId, report }: SuggestionsTabProps) {
  const suggestions = report.suggestionStates;
  const { enqueue, activeByKey } = useOptimizationQueue(executionId, report.reportDate);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const links = useMemo(() => dv360Context(asDeliveryReport(report.report)), [report.report]);

  const selectable = suggestions.filter((suggestion) =>
    report.actionable && isSelectable(suggestion, activeByKey.has(suggestion.key))
  );
  // Rows that left the selectable set (decided, queued) drop out of the selection.
  const chosen = selectable.filter((suggestion) => selected.has(suggestion.key));
  const allChosen = selectable.length > 0 && chosen.length === selectable.length;

  const toggle = (key: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected(allChosen ? new Set() : new Set(selectable.map((suggestion) => suggestion.key)));
  };
  const run = (items: OptimizationSuggestionState[], decision: SuggestionDecision) => {
    if (items.length === 0) return;
    enqueue(items, decision);
    setSelected((current) => {
      const next = new Set(current);
      for (const item of items) next.delete(item.key);
      return next;
    });
  };

  return (
    <ReportSection title="Sugerencias" count={suggestions.length} subtitle="en el orden del informe">
      {!report.actionable && (
        <div className="adv-alert mb-3 text-adv-body-s">
          Este informe es del {fullDate(report.reportDate)}: sus sugerencias ya no se pueden aplicar.
          Ejecuta /vera:delivery-report para tener uno de hoy.
        </div>
      )}

      {suggestions.length === 0 ? (
        <p className="text-adv-body-s text-adv-tertiary py-2">
          Sin cambios propuestos: KPIs en banda y entrega cubierta.
        </p>
      ) : (
        <>
          <SelectionBar
            chosen={chosen}
            onRun={run}
            onClear={() => setSelected(new Set())}
          />
          <TableFrame
            columnWidths={COLUMN_WIDTHS}
            head={
              <>
                <Th>
                  <input
                    type="checkbox"
                    className="adv-check"
                    checked={allChosen}
                    disabled={selectable.length === 0}
                    onChange={toggleAll}
                    aria-label="Seleccionar todas las pendientes"
                  />
                </Th>
                <Th>Cambio</Th>
                <Th>Entidad</Th>
                <Th>Antes → después</Th>
                <Th>Motivo</Th>
                <Th>Estado</Th>
              </>
            }
          >
            {suggestions.map((suggestion) => (
              <SuggestionRow
                key={suggestion.key}
                suggestion={suggestion}
                job={activeByKey.get(suggestion.key) ?? null}
                actionable={report.actionable}
                selected={selected.has(suggestion.key)}
                links={links}
                onToggle={() => toggle(suggestion.key)}
                onRun={(decision) => run([suggestion], decision)}
              />
            ))}
          </TableFrame>
        </>
      )}
    </ReportSection>
  );
}

interface SelectionBarProps {
  chosen: OptimizationSuggestionState[];
  onRun: (items: OptimizationSuggestionState[], decision: SuggestionDecision) => void;
  onClear: () => void;
}

/** Bulk actions on the selected rows; each button counts the rows it applies to. */
export function SelectionBar({ chosen, onRun, onClear }: SelectionBarProps) {
  if (chosen.length === 0) return null;
  const applicable = chosen.filter((suggestion) => suggestion.applicable);
  const manual = chosen.filter((suggestion) => suggestion.manual);

  return (
    <div className="sticky top-0 z-10 mb-2 flex flex-wrap items-center gap-2 rounded-adv-lg border border-adv-border bg-adv-sunken px-3 py-2">
      <span className="text-adv-body-s font-bold mr-auto">
        {chosen.length} {chosen.length === 1 ? 'seleccionada' : 'seleccionadas'}
      </span>
      <button
        type="button"
        onClick={() => onRun(applicable, 'approve')}
        disabled={applicable.length === 0}
        className="adv-btn adv-btn--primary adv-btn--sm"
      >
        <Check size={14} />
        Aplicar en DV360 ({applicable.length})
      </button>
      <button
        type="button"
        onClick={() => onRun(manual, 'mark-applied')}
        disabled={manual.length === 0}
        className="adv-btn adv-btn--secondary adv-btn--sm"
      >
        <CheckCheck size={14} />
        Marcar como realizadas ({manual.length})
      </button>
      <button type="button" onClick={() => onRun(chosen, 'reject')} className="adv-btn adv-btn--secondary adv-btn--sm">
        <X size={14} />
        Descartar ({chosen.length})
      </button>
      <button type="button" onClick={onClear} className="adv-btn adv-btn--ghost adv-btn--sm">
        Quitar selección
      </button>
    </div>
  );
}

interface SuggestionRowProps {
  suggestion: OptimizationSuggestionState;
  /** Its queued or running decision, if any. */
  job: QueueJob | null;
  actionable: boolean;
  selected: boolean;
  links: Dv360Context;
  onToggle: () => void;
  onRun: (decision: SuggestionDecision) => void;
}

/** The DV360 page of the entity a suggestion changes; none for execution-level ones. */
export function suggestionEntityUrl(suggestion: OptimizationSuggestionState, links: Dv360Context): string | null {
  if (suggestion.level === 'execution') return null;
  return dv360Url(
    {
      advertiserId: suggestion.advertiser_id,
      insertionOrderId: suggestion.insertion_order_id,
      lineItemId: suggestion.line_item_id,
    },
    links
  );
}

function SuggestionRow({ suggestion, job, actionable, selected, links, onToggle, onRun }: SuggestionRowProps) {
  const priority = priorityTag(suggestion.priority);
  const entityId = suggestion.line_item_id ?? suggestion.insertion_order_id;
  const canSelect = actionable && isSelectable(suggestion, job !== null);
  const entityUrl = suggestionEntityUrl(suggestion, links);

  return (
    <tr className={selected ? 'bg-adv-sunken' : 'hover:bg-adv-sunken'}>
      <Td>
        {suggestion.lever !== 'kpi' && (
          <input
            type="checkbox"
            className="adv-check"
            checked={selected && canSelect}
            disabled={!canSelect}
            onChange={onToggle}
            aria-label={`Seleccionar ${suggestion.entity_name ?? suggestion.text}`}
          />
        )}
      </Td>
      <Td>
        <span className={`adv-tag ${priority.className}`}>{priority.label}</span>
        <p className="mt-1">{leverLabel(suggestion.lever, suggestion.bid_type)}</p>
        {suggestion.kind && <p className="text-adv-body-s text-adv-tertiary">{suggestion.kind}</p>}
      </Td>
      <Td>
        {entityUrl ? (
          <a
            href={entityUrl}
            target="_blank"
            rel="noreferrer"
            className="font-bold hover:underline break-words"
            title="Abrir en DV360"
          >
            {suggestion.entity_name ?? 'Campaña'}
            <ExternalLink size={12} className="inline ml-1 align-baseline text-adv-tertiary" />
          </a>
        ) : (
          <span className="font-bold break-words">{suggestion.entity_name ?? 'Campaña'}</span>
        )}
        <p className="text-adv-body-s text-adv-tertiary break-words">
          {LEVEL_LABELS[suggestion.level]}
          {entityId && <span className="adv-num"> · {entityId}</span>}
        </p>
      </Td>
      <Td>
        <ChangeCell suggestion={suggestion} />
      </Td>
      <Td className="text-adv-secondary">
        <p className="line-clamp-3" title={suggestion.reason}>
          {suggestion.reason}
        </p>
      </Td>
      <Td>
        <StatusCell suggestion={suggestion} job={job} />
        <RowActions suggestion={suggestion} job={job} actionable={actionable} onRun={onRun} />
      </Td>
    </tr>
  );
}

export function ChangeCell({ suggestion }: { suggestion: OptimizationSuggestionState }) {
  if (suggestion.before === null && suggestion.after === null) {
    return <span className="break-words">{suggestion.text}</span>;
  }
  const applied =
    suggestion.appliedValue !== null && suggestion.appliedValue !== suggestion.after
      ? suggestion.appliedValue
      : null;

  return (
    <span className="adv-num">
      {formatSuggestionValue(suggestion.unit, suggestion.before, suggestion.lever)}
      <span className="text-adv-tertiary"> → </span>
      <span className="font-bold">
        {formatSuggestionValue(suggestion.unit, suggestion.after, suggestion.lever)}
      </span>
      {applied !== null && (
        <span className="block text-adv-body-s text-adv-accent font-adv-sans">
          aplicado {formatSuggestionValue(suggestion.unit, applied, suggestion.lever)}
        </span>
      )}
    </span>
  );
}

export function StatusCell({ suggestion, job }: { suggestion: OptimizationSuggestionState; job: QueueJob | null }) {
  if (suggestion.lever === 'kpi') {
    return <span className="adv-tag adv-tag--default">Informativa</span>;
  }
  if (job) {
    return job.status === 'running' ? (
      <span className="adv-tag adv-tag--info inline-flex items-center gap-1">
        <Loader2 size={12} className="animate-spin" />
        Aplicando…
      </span>
    ) : (
      <span className="adv-tag adv-tag--outline">En cola</span>
    );
  }
  const tag = STATUS_TAGS[suggestion.status];
  return (
    <div className="space-y-1">
      <span className={`adv-tag ${tag.className}`}>{tag.label}</span>
      {suggestion.decidedAt && (
        <p className="text-adv-body-s text-adv-tertiary break-words">
          {suggestion.decidedBy ?? ''} · <span className="adv-num">{dateTime(suggestion.decidedAt)}</span>
        </p>
      )}
      {suggestion.adviaOsRecordId && (
        <a
          href={adviaOsRecordUrl(suggestion.adviaOsRecordId)}
          target="_blank"
          rel="noreferrer"
          className="text-adv-body-s text-adv-accent hover:underline inline-flex items-center gap-1"
        >
          Ver en Advia OS
          <ExternalLink size={12} />
        </a>
      )}
      {suggestion.error && (
        <p className="text-adv-body-s text-adv-warning-text break-words" title={suggestion.error}>
          {suggestion.status === 'stale' ? 'DV360 ha cambiado desde el informe: no se ha aplicado' : suggestion.error}
        </p>
      )}
    </div>
  );
}

interface RowActionsProps {
  suggestion: OptimizationSuggestionState;
  job: QueueJob | null;
  actionable: boolean;
  onRun: (decision: SuggestionDecision) => void;
}

/** Compact icon buttons under the status: each one queues its decision straight away. */
export function RowActions({ suggestion, job, actionable, onRun }: RowActionsProps) {
  if (suggestion.lever === 'kpi' || job) return null;

  if (needsRecordRetry(suggestion)) {
    return (
      <button
        type="button"
        onClick={() => onRun(suggestion.manual ? 'mark-applied' : 'approve')}
        className="adv-btn adv-btn--secondary adv-btn--sm mt-2"
      >
        <RotateCcw size={14} />
        Reintentar registro
      </button>
    );
  }
  // The backend only decides pending suggestions; the rest are final.
  if (suggestion.status !== 'pending') return null;

  const hint = actionable ? undefined : 'Solo se aplican sugerencias del informe de hoy o de ayer';
  return (
    <div className="flex items-center gap-1 mt-2" title={hint}>
      {suggestion.applicable && (
        <IconAction label="Aplicar en DV360" disabled={!actionable} primary onClick={() => onRun('approve')}>
          <Check size={16} />
        </IconAction>
      )}
      {suggestion.manual && (
        <IconAction label="Marcar como realizada" disabled={!actionable} onClick={() => onRun('mark-applied')}>
          <CheckCheck size={16} />
        </IconAction>
      )}
      <IconAction label="Descartar" disabled={!actionable} onClick={() => onRun('reject')}>
        <X size={16} />
      </IconAction>
    </div>
  );
}

interface IconActionProps {
  label: string;
  disabled: boolean;
  primary?: boolean;
  onClick: () => void;
  children: ReactNode;
}

function IconAction({ label, disabled, primary = false, onClick, children }: IconActionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={`adv-btn adv-btn--icon adv-btn--sm ${primary ? 'adv-btn--primary' : 'adv-btn--secondary'}`}
    >
      {children}
    </button>
  );
}
